import { IComparisonData } from "components-for/display/comparison-table/comparison-table.types";
import { IDownloadAgentsButtonOptions } from "./download-comparison-table-button.types";

import {
    EXPERIENCE_SCORE_METRIC_ENABLED,
    GRAMMAR_METRIC_ENABLED,
    SPELLING_METRIC_ENABLED,
} from "constants/feature-flags";

/**
 * Format agent data to CVS
 * @param data
 */
const formatCsvData = <Group extends {}>(
    data: IComparisonData<Group>,
    headingProperties: { [index: string]: (group: Group) => any },
    options: IDownloadAgentsButtonOptions
) => {
    const headingValues = Object.values(headingProperties).map((getter) => {
        return data.comparisonGroup === null ? "Overall" : getter(data.comparisonGroup);
    });

    return headingValues
        .concat([
            data.count,
            data.meanHandlingTimeSeconds,
            data.meanResponseSeconds,
            data.netPromoterScore,
            data.customerSatisfactionScore?.toFixed(2),
            data.queryResolutionRate?.toFixed(2),
            ...(EXPERIENCE_SCORE_METRIC_ENABLED ? [data.meanExperienceScore?.toFixed(2)] : []),
            data.percentageFrustration?.toFixed(2),
            data.percentagePraise?.toFixed(2),
            ...(options.hasPercentageTag ? (data.tagValues ?? []).map((value) => value.toFixed(2)) : []),
            ...(SPELLING_METRIC_ENABLED ? [data.percentageSpelling?.toFixed(2)] : []),
            ...(GRAMMAR_METRIC_ENABLED ? [data.percentageGrammar?.toFixed(2)] : []),
        ])
        .join(",");
};

/**
 * Generate agents CSV file contents using provided data
 * @param csvKeys
 * @param comparisonGroup
 * @param overallData
 * @returns CSV string
 */
export const generateAgentsCsvFileContents = <Group extends {}>(
    csvKeys: string[],
    headingProperties: { [index: string]: (group: Group) => any },
    comparisonGroup: IComparisonData<Group>[],
    overallData: IComparisonData<Group>,
    options: IDownloadAgentsButtonOptions
) => {
    const csvHeader = csvKeys.map((key) => key).join(",");

    const csvOverallDataBody = formatCsvData(overallData, headingProperties, options);

    const csvAgentsDataBody = comparisonGroup
        .map((agentData) => formatCsvData(agentData, headingProperties, options))
        .join("\r\n");
    return [csvHeader, csvOverallDataBody, csvAgentsDataBody].join("\r\n");
};
